import type { Locale } from "@/i18n/translations";

export type LocalizedText = Record<Locale, string>;

export type ChecklistTask = {
  id: string;
  label: LocalizedText;
  detail?: LocalizedText;
};

export type ChecklistPhase = {
  id: string;
  /** How far before the wedding this phase starts. Use daysBefore for the final week. */
  monthsBefore?: number;
  daysBefore?: number;
  /** Phases that happen after the wedding day. */
  after?: boolean;
  label: LocalizedText;
  summary: LocalizedText;
  tasks: ChecklistTask[];
};

export const CHECKLIST_PHASES: ChecklistPhase[] = [
  {
    id: "p12",
    monthsBefore: 12,
    label: { en: "12 months before", fr: "12 mois avant" },
    summary: {
      en: "Decisions that everything else depends on: the money, the size, the date.",
      fr: "Les décisions dont tout le reste dépend : le budget, la taille, la date.",
    },
    tasks: [
      {
        id: "budget-total",
        label: { en: "Agree on a total budget and who contributes what", fr: "Fixer le budget total et qui contribue à quoi" },
        detail: {
          en: "Write the number down before you look at anything. Every later choice is measured against it.",
          fr: "Notez le montant avant de regarder quoi que ce soit. Chaque choix se mesurera à ce chiffre.",
        },
      },
      {
        id: "guest-estimate",
        label: { en: "Estimate your guest count", fr: "Estimer le nombre d'invités" },
        detail: {
          en: "Guest count drives venue, catering and stationery. An estimate within ten people is enough for now.",
          fr: "Le nombre d'invités détermine le lieu, le traiteur et la papeterie. Une estimation à dix près suffit.",
        },
      },
      {
        id: "date-window",
        label: { en: "Pick a date, or two or three possible dates", fr: "Choisir une date, ou deux ou trois dates possibles" },
      },
      {
        id: "style",
        label: { en: "Decide the style and formality together", fr: "Décider ensemble du style et du niveau de formalité" },
      },
      {
        id: "venue-visits",
        label: { en: "Shortlist and visit venues", fr: "Présélectionner et visiter les lieux" },
        detail: {
          en: "Ask what the price includes, the capacity, the end time, and the cancellation terms.",
          fr: "Demandez ce que le prix inclut, la capacité, l'heure de fin et les conditions d'annulation.",
        },
      },
      {
        id: "venue-book",
        label: { en: "Book the venue and confirm the date", fr: "Réserver le lieu et confirmer la date" },
      },
      {
        id: "wedding-party",
        label: { en: "Ask the people you want beside you", fr: "Demander aux personnes que vous voulez à vos côtés" },
      },
    ],
  },
  {
    id: "p9",
    monthsBefore: 9,
    label: { en: "9 months before", fr: "9 mois avant" },
    summary: {
      en: "Book the providers who only take one wedding a day.",
      fr: "Réserver les prestataires qui ne prennent qu'un mariage par jour.",
    },
    tasks: [
      {
        id: "officiant",
        label: { en: "Confirm the officiant or celebrant", fr: "Confirmer l'officiant ou le célébrant" },
      },
      { id: "photographer", label: { en: "Book the photographer", fr: "Réserver le ou la photographe" } },
      { id: "videographer", label: { en: "Book the videographer, if you want one", fr: "Réserver la vidéo, si vous en voulez" } },
      { id: "caterer", label: { en: "Book the caterer, or confirm the venue's catering", fr: "Réserver le traiteur, ou confirmer celui du lieu" } },
      { id: "music", label: { en: "Book the band or DJ", fr: "Réserver le groupe ou le DJ" } },
      {
        id: "guest-list",
        label: { en: "Build the real guest list with addresses", fr: "Construire la vraie liste d'invités avec les adresses" },
        detail: {
          en: "Names, emails and postal addresses in one place. This is the list you will reuse a dozen times.",
          fr: "Noms, courriels et adresses postales au même endroit. Vous réutiliserez cette liste une dizaine de fois.",
        },
      },
      { id: "save-the-date", label: { en: "Send save-the-dates", fr: "Envoyer les save-the-date" } },
      {
        id: "accommodation",
        label: { en: "Look into accommodation for guests travelling in", fr: "Prévoir l'hébergement des invités qui viennent de loin" },
      },
    ],
  },
  {
    id: "p6",
    monthsBefore: 6,
    label: { en: "6 months before", fr: "6 mois avant" },
    summary: {
      en: "Outfits, paperwork and the providers that shape the day's look.",
      fr: "Tenues, démarches administratives et prestataires qui donnent le ton.",
    },
    tasks: [
      { id: "outfits", label: { en: "Order your outfits", fr: "Commander vos tenues" }, detail: { en: "Ask for the delivery date in writing; alterations need weeks, not days.", fr: "Demandez la date de livraison par écrit : les retouches prennent des semaines, pas des jours." } },
      { id: "florist", label: { en: "Book the florist", fr: "Réserver le ou la fleuriste" } },
      { id: "cake", label: { en: "Book the cake or dessert", fr: "Réserver le gâteau ou le dessert" } },
      { id: "hair-makeup", label: { en: "Book hair and makeup, and schedule a trial", fr: "Réserver coiffure et maquillage, et prévoir un essai" } },
      { id: "legal", label: { en: "Check the legal requirements where you are marrying", fr: "Vérifier les démarches légales là où vous vous mariez" }, detail: { en: "Rules and waiting periods differ by country, province and state. Check the official source for your location.", fr: "Les règles et les délais varient selon le pays et la province. Vérifiez la source officielle de votre lieu." } },
      { id: "transport", label: { en: "Arrange transport for the two of you and, if needed, for guests", fr: "Organiser le transport pour vous deux et, au besoin, pour les invités" } },
      { id: "registry", label: { en: "Decide on gifts: registry, contribution, or nothing at all", fr: "Décider pour les cadeaux : liste, contribution, ou rien du tout" } },
      { id: "honeymoon", label: { en: "Book the honeymoon, and check passport expiry dates", fr: "Réserver le voyage de noces, et vérifier la validité des passeports" } },
    ],
  },
  {
    id: "p4",
    monthsBefore: 4,
    label: { en: "4 months before", fr: "4 mois avant" },
    summary: {
      en: "Invitations go out, and the day starts to have a shape.",
      fr: "Les invitations partent, et la journée prend forme.",
    },
    tasks: [
      { id: "invitations-order", label: { en: "Order invitations and the rest of the stationery", fr: "Commander les invitations et le reste de la papeterie" } },
      { id: "rsvp-method", label: { en: "Decide how guests will RSVP, and by when", fr: "Décider comment les invités répondront, et avant quelle date" }, detail: { en: "One method, one deadline. Chasing replies across text, email and paper is where most of the work hides.", fr: "Une seule méthode, une seule date limite. Courir après les réponses par SMS, courriel et papier est ce qui prend le plus de temps." } },
      { id: "menu", label: { en: "Taste and confirm the menu, including dietary options", fr: "Goûter et confirmer le menu, avec les options alimentaires" } },
      { id: "ceremony-outline", label: { en: "Draft the ceremony outline with your officiant", fr: "Préparer le déroulé de la cérémonie avec l'officiant" } },
      { id: "readings-music", label: { en: "Choose readings and ceremony music", fr: "Choisir les lectures et la musique de cérémonie" } },
      { id: "rentals", label: { en: "Confirm rentals: tables, chairs, linens, lighting", fr: "Confirmer les locations : tables, chaises, nappes, éclairage" } },
      { id: "rehearsal", label: { en: "Plan the rehearsal and any welcome gathering", fr: "Planifier la répétition et l'éventuel accueil de la veille" } },
    ],
  },
  {
    id: "p2",
    monthsBefore: 2,
    label: { en: "2 months before", fr: "2 mois avant" },
    summary: {
      en: "Send, chase, and turn plans into a written run-of-show.",
      fr: "Envoyer, relancer, et transformer les plans en déroulé écrit.",
    },
    tasks: [
      { id: "invitations-send", label: { en: "Send the invitations", fr: "Envoyer les invitations" } },
      { id: "timeline-draft", label: { en: "Draft the day-of timeline, hour by hour", fr: "Rédiger le déroulé du jour J, heure par heure" }, detail: { en: "Start from the ceremony time and work outwards in both directions.", fr: "Partez de l'heure de la cérémonie et remontez dans les deux sens." } },
      { id: "vows", label: { en: "Write your vows or confirm the wording you will use", fr: "Écrire vos vœux ou confirmer le texte retenu" } },
      { id: "fittings", label: { en: "Attend fittings and book the final one", fr: "Faire les essayages et fixer le dernier" } },
      { id: "budget-review", label: { en: "Review the budget: paid, quoted, still due", fr: "Revoir le budget : payé, soumissionné, reste dû" } },
      { id: "provider-contacts", label: { en: "Collect every provider's contact and arrival time", fr: "Rassembler les coordonnées et heures d'arrivée de chaque prestataire" } },
    ],
  },
  {
    id: "p1",
    monthsBefore: 1,
    label: { en: "1 month before", fr: "1 mois avant" },
    summary: {
      en: "Close the guest list and hand the plan to everyone who needs it.",
      fr: "Clôturer la liste d'invités et transmettre le plan à ceux qui en ont besoin.",
    },
    tasks: [
      { id: "rsvp-chase", label: { en: "Chase the missing RSVPs", fr: "Relancer les RSVP manquants" } },
      { id: "final-count", label: { en: "Give the caterer the final head count", fr: "Donner le nombre final au traiteur" } },
      { id: "seating", label: { en: "Decide where people sit, if you are seating guests", fr: "Décider du placement, si vous placez vos invités" } },
      { id: "payments", label: { en: "Schedule the remaining payments and final balances", fr: "Planifier les paiements restants et les soldes" } },
      { id: "timeline-share", label: { en: "Share the timeline with providers and the wedding party", fr: "Partager le déroulé avec les prestataires et les proches impliqués" } },
      { id: "legal-docs", label: { en: "Confirm the licence or paperwork is in hand", fr: "Vérifier que la licence ou les documents sont en main" } },
      { id: "beauty-final", label: { en: "Final fitting, haircut and any beauty appointments", fr: "Dernier essayage, coiffure et rendez-vous beauté" } },
    ],
  },
  {
    id: "week",
    daysBefore: 7,
    label: { en: "The week of", fr: "La semaine du mariage" },
    summary: {
      en: "Confirm, pack, delegate. Nothing new should be added here.",
      fr: "Confirmer, préparer, déléguer. Rien de nouveau ne devrait s'ajouter ici.",
    },
    tasks: [
      { id: "confirm-all", label: { en: "Confirm times with every provider", fr: "Confirmer les horaires avec chaque prestataire" } },
      { id: "weather", label: { en: "Check the forecast and confirm the wet-weather plan", fr: "Vérifier la météo et confirmer le plan B en cas de pluie" } },
      { id: "delegate", label: { en: "Give one trusted person the timeline and the phone numbers", fr: "Confier le déroulé et les numéros à une personne de confiance" }, detail: { en: "So nobody has to ask you a logistics question on the day.", fr: "Pour que personne n'ait à vous poser de question logistique le jour même." } },
      { id: "pack", label: { en: "Pack outfits, rings, documents and an emergency kit", fr: "Préparer tenues, alliances, documents et une trousse de secours" } },
      { id: "envelopes", label: { en: "Prepare tips, final payments and thank-you envelopes", fr: "Préparer pourboires, derniers paiements et enveloppes de remerciement" } },
      { id: "rest", label: { en: "Block out rest: eat, sleep, and stop deciding things", fr: "Réserver du repos : manger, dormir, arrêter de décider" } },
    ],
  },
  {
    id: "after",
    after: true,
    label: { en: "After the wedding", fr: "Après le mariage" },
    summary: {
      en: "The short list that is easy to forget once it is over.",
      fr: "La courte liste qu'on oublie facilement une fois la fête passée.",
    },
    tasks: [
      { id: "returns", label: { en: "Return rentals and outfits", fr: "Rendre les locations et les tenues" } },
      { id: "final-invoices", label: { en: "Settle the final invoices", fr: "Régler les dernières factures" } },
      { id: "thank-you", label: { en: "Send thank-you notes", fr: "Envoyer les remerciements" } },
      { id: "reviews", label: { en: "Leave reviews for the providers you would recommend", fr: "Laisser un avis aux prestataires que vous recommanderiez" } },
      { id: "names", label: { en: "Handle any name or document changes", fr: "Effectuer les changements de nom ou de documents" } },
      { id: "photos", label: { en: "Choose and order your photos", fr: "Choisir et commander vos photos" } },
    ],
  },
];

export const TOTAL_TASKS = CHECKLIST_PHASES.reduce((n, p) => n + p.tasks.length, 0);
